import React, { useCallback, useContext, useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import { map } from "ramda";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import AddIcon from "@mui/icons-material/Add";
import {
  useOrganizationKeystore,
  useOrganizationKeystoreCreate,
  useOrganizationKeystoreDelete,
} from "../organizationServices";
import { isBlank, notBlank } from "../../util/ext";
import PanelLoadingIndicator from "../../components/PanelLoadingIndicator";
import makeStyles from "@mui/styles/makeStyles";
import ListItemSecondaryAction from "@mui/material/ListItemSecondaryAction";
import { AlertContext } from "../../components/alert/AlertProvider";
import ConfirmButton from "../../components/ConfirmButton";

const useStyles = makeStyles(theme => ({
  header: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },

  content: {
    paddingTop: 0,
  },
}));

const OrganizationKeystoreCard = ({ id }) => {
  const classes = useStyles();
  const { alertError } = useContext(AlertContext);
  const [keystore, setKeystore] = useState([]);

  const keystoreResponse = useOrganizationKeystore(id);
  useEffect(() => {
    if (keystoreResponse.isSuccess && notBlank(keystoreResponse.data)) {
      setKeystore(keystoreResponse.data);
    } else {
      setKeystore([]);
    }
  }, [keystoreResponse.isSuccess, keystoreResponse.data]);

  const keystoreDelete = useOrganizationKeystoreDelete();
  const handleDeleteClick = useCallback(
    async accessKey => {
      try {
        await keystoreDelete.mutateAsync({
          resourceIds: { orgId: id, accessKey },
        });
        await keystoreResponse.refetch();
      } catch (err) {
        alertError(err);
      }
    },
    [id, keystoreDelete, keystoreResponse, alertError],
  );

  const keystoreCreate = useOrganizationKeystoreCreate();
  const handleAddClick = useCallback(
    async accessKey => {
      try {
        await keystoreCreate.mutateAsync({ resourceIds: { orgId: id } });
        await keystoreResponse.refetch();
      } catch (err) {
        alertError(err);
      }
    },
    [id, keystoreCreate, keystoreResponse, alertError],
  );

  return (
    <Card raised elevation={4} style={{ height: "100%" }}>
      <CardHeader
        titleTypographyProps={{
          variant: "subtitle1",
          color: "primary",
        }}
        title="Keystore"
        action={
          <Tooltip title="Add Association">
            <IconButton onClick={handleAddClick} size="large">
              <AddIcon color="primary" fontSize="large" />
            </IconButton>
          </Tooltip>
        }
        classes={{ root: classes.header }}
      />
      <CardContent classes={{ root: classes.content }}>
        {keystoreResponse.isError && (
          <Box padding={5} color="error" fontSize="1.4rem" fontStyle="italic">
            Error loading Organizations Keystore.
          </Box>
        )}
        {keystoreResponse.isLoading && <PanelLoadingIndicator />}
        {keystoreResponse.isSuccess && isBlank(keystore) && (
          <Box padding={5} fontSize="1.4rem" fontStyle="italic">
            No Keystore set up for this Organization.
          </Box>
        )}
        {keystoreResponse.isSuccess && notBlank(keystore) && (
          <List dense>
            {map(
              ks => (
                <ListItem key={ks.accessKey}>
                  <ListItemText
                    primaryTypographyProps={{ component: "div" }}
                    primary={
                      <Box mb={1}>
                        <div>Access Key:</div>
                        <div>{ks.accessKey}</div>
                      </Box>
                    }
                    secondaryTypographyProps={{ component: "div" }}
                    secondary={
                      <Box>
                        <div>Secret Key:</div>
                        <div>{ks.secretKey}</div>
                      </Box>
                    }
                  />
                  <ListItemSecondaryAction>
                    <ConfirmButton
                      handleDelete={() => handleDeleteClick(ks.accessKey)}
                      dialogTitle="Remove Access Key"
                      dialogBody="Are you sure you want to remove this access key?"
                    />
                  </ListItemSecondaryAction>
                </ListItem>
              ),
              keystore,
            )}
          </List>
        )}
      </CardContent>
    </Card>
  );
};

export default OrganizationKeystoreCard;
